import { useState } from 'react';
import { useNavigate } from 'react-router';
import { PolicyEscalationType, langauge } from 'shared';
import { toast } from 'react-toastify';

import { Modal } from '../../../common/Atoms/Modal';
import { Select } from '../../../common/Atoms/Select';
import { TextArea } from '../../../common/Atoms/TextArea';
import { InputTextAddon } from '../../../common/Atoms/InputTextAddon';
import { FormLabel } from '../../../common/Atoms/FormLabel';
import { Button } from '../../../common/Atoms/Button';
import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import { useCreateEscalationMutation } from '../../services/api/escalationApi/escalation';
import { Assignee, RECORD_ASSIGNEE } from '../Common/Assignee';
import { ContactSearch, IPickedContact } from '../Common/ContactSearch';

interface IRaiseEscalationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Fixed contact. When omitted the user searches for one */
  contact?: IPickedContact;
  /** Makes it an invoice level escalation, otherwise it covers the contact */
  invoiceId?: string;
  /** Raise one escalation per contact (for example from an AI suggestion). Takes precedence over `contact` */
  contacts?: IPickedContact[];
  defaultType?: PolicyEscalationType;
  defaultSteps?: string;
  defaultAssignedUserId?: string | null;
  /** Called with one line per contact instead of opening the escalation. Only fires when at least one was raised */
  onRaised?: (results: string[]) => void;
}

export function RaiseEscalationModal({
  open,
  setOpen,
  contact,
  invoiceId,
  contacts,
  defaultType = PolicyEscalationType.CALL,
  defaultSteps = ``,
  defaultAssignedUserId = null,
  onRaised,
}: IRaiseEscalationModalProps) {
  const organisation = useGetSelectedOrganisation();
  const navigate = useNavigate();
  const [createEscalation, { isLoading }] = useCreateEscalationMutation();
  const [pickedContact, setPickedContact] = useState<IPickedContact | undefined>();
  const [type, setType] = useState(defaultType);
  const [steps, setSteps] = useState(defaultSteps);
  const [assignedUserId, setAssignedUserId] = useState<string | null>(defaultAssignedUserId);
  const [notifyEmails, setNotifyEmails] = useState(``);

  const targetContacts = contacts ?? [contact || pickedContact].filter((picked): picked is IPickedContact => !!picked);

  function close() {
    setOpen(false);
    setPickedContact(undefined);
    setType(defaultType);
    setSteps(defaultSteps);
    setAssignedUserId(defaultAssignedUserId);
    setNotifyEmails(``);
  }

  async function onSave() {
    if (!targetContacts.length) return;

    const results: string[] = [];
    let lastId: number | null = null;

    for (const target of targetContacts) {
      try {
        ({ id: lastId } = await createEscalation({
          organisationId: organisation.id,
          contactId: target.id,
          invoiceId: invoiceId || null,
          type,
          steps: steps.trim() || null,
          assignedUserId: assignedUserId === RECORD_ASSIGNEE ? null : assignedUserId,
          assignToRecordAssignee: assignedUserId === RECORD_ASSIGNEE,
          notifyEmails: notifyEmails.trim() || null,
        }).unwrap());

        results.push(`${target.name}: escalation raised`);
      }
      catch (err) {
        const data = (err as { data?: unknown })?.data;
        const message = typeof data === `string` ? data : `Could not raise the escalation`;

        toast.error(targetContacts.length > 1 ? `${target.name}: ${message}` : message);
        results.push(`${target.name}: failed (${message})`);
      }
    }

    if (lastId === null) return;

    close();

    if (onRaised) {
      onRaised(results);

      return;
    }

    navigate(`/${organisation.id}/escalations/${lastId}/contact/${targetContacts[0].id}`);
  }

  return (
    <Modal
      title={ `Raise Escalation` }
      open={ open }
      setOpen={ value => value ? setOpen(true) : close() }
    >
      <div className={ `space-y-4` }>
        { contacts ? (
          <div>
            <FormLabel>{ contacts.length === 1 ? `Contact` : `Contacts (${contacts.length})` }</FormLabel>
            <p className={ `text-sm text-gray-700` }>{ contacts.map(target => target.name).join(`, `) }</p>
          </div>
        ) : contact ? null : (
          <ContactSearch
            label={ `Contact` }
            selected={ pickedContact }
            onSelect={ setPickedContact }
          />
        ) }

        <Select
          label={ `Type` }
          name={ `type` }
          options={ Object.values(PolicyEscalationType).map(value => ({
            label: langauge.escalation.type[value].label,
            value,
          })) }
          selected={ type }
          onChange={ ({ value }) => setType(value as PolicyEscalationType) }
        />

        <TextArea
          label={ `Instructions` }
          name={ `steps` }
          value={ steps }
          onChange={ ({ value }) => setSteps(String(value)) }
          placeholder={ `What needs to happen` }
        />

        <div>
          <FormLabel>{ `Assigned User` }</FormLabel>
          <Assignee
            current={ assignedUserId }
            onChange={ setAssignedUserId }
            allowRecordAssignee
          />
        </div>

        <InputTextAddon
          label={ `Notify email addresses` }
          name={ `notifyEmails` }
          value={ notifyEmails }
          onChange={ ({ value }) => setNotifyEmails(String(value)) }
          helpIcon={ `Enter up to 5 email addresses, separated by a comma` }
        />

        <div className={ `flex justify-end` }>
          <Button
            color={ `paidniceGreenOutline` }
            onClick={ onSave }
            loading={ isLoading }
            disabled={ isLoading || !targetContacts.length }
          >
            { targetContacts.length > 1 ? `Raise ${targetContacts.length} Escalations` : `Raise Escalation` }
          </Button>
        </div>
      </div>
    </Modal>
  );
}
